import { CSSProperties, ReactNode, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';

import { RiArrowDownFill } from 'react-icons/ri';

import { Typography } from '@mui/material';

import { NumberFormatOptions } from '@formatjs/ecma402-abstract';
import { CustomFormatConfig } from '@formatjs/intl';
import styled from 'styled-components';

export interface MetricMeasurementBarProps {
  /**
   * The background of the measurement bar.
   */
  background: CSSProperties['background'];
  className?: string;
  /**
   * Lower bound of the metric's possible range.
   */
  lowerBound: number;
  /**
   * Label to put at the lower end of the metric bar. Ideally short.
   */
  lowerLabel: ReactNode;
  /**
   * The metric to mark on the measurement bar.
   */
  metric: number;
  numberFormatOptions: NumberFormatOptions & CustomFormatConfig;
  /**
   * The uppor bound of the metrics possible range.
   */
  upperBound: number;
  /**
   * Label to put at the upper end of the metric bar. Ideally short.
   */
  upperLabel: ReactNode;
}

const StyledMetricMeasurementBar = styled.div<{ $background: CSSProperties['background']; $percent: number }>`
  .indicator-bar,
  .labels {
    width: 100%;
  }

  .indicator-bar {
    background: ${({ $background }) => $background};
    height: 22px;
    margin-top: ${({ theme }) => theme.spacing(1)};
  }

  .labels {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .higher-label {
    transform: translate(50%, 0);
  }

  .lower-label {
    transform: translate(-50%, 0);
  }

  .arrow {
    transform: rotate(180deg);
  }

  .value-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 0px;
    position: relative;
    left: ${({ $percent }) => `${$percent * 100}%`};
  }
`;

export const MetricMeasurementBar = ({
  className,
  background,
  lowerBound,
  lowerLabel,
  metric,
  numberFormatOptions,
  upperBound,
  upperLabel,
}: MetricMeasurementBarProps) => {
  const percent = useMemo(() => {
    const denominator = upperBound - lowerBound;
    return denominator !== 0 ? metric / denominator : 0;
  }, [lowerBound, metric, upperBound]);

  return (
    <StyledMetricMeasurementBar $background={background} className={className} $percent={percent}>
      <div className="value-indicator">
        <Typography className="percent" variant="body1">
          <FormattedNumber value={metric} {...numberFormatOptions} />
        </Typography>
        <RiArrowDownFill className="arrow" />
      </div>
      <div className="indicator-bar" />
      <div className="labels">
        <Typography className="lower-label" variant="body2">
          {lowerLabel}
        </Typography>
        <Typography className="higher-label" variant="body2">
          {upperLabel}
        </Typography>
      </div>
    </StyledMetricMeasurementBar>
  );
};
